/**
 * The two non-conversation rows in the chat panel's history menu.
 *
 * Extracted from BirdChatPanel because they are presentational and because the
 * panel is at its complexity ceiling — every conditional added there is one the
 * linter counts against the whole component.
 */

import { Flex, MenuDivider, MenuItem } from '@chakra-ui/react';
import { SessionList } from './SessionList';
import type { ComponentProps } from 'react';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { BowerSpinner } from '../../ui/BowerSpinner';

/**
 * Opens the command palette pre-scoped to Chats.
 *
 * A row rather than an input: Chakra's MenuList implements typeahead and
 * consumes printable keystrokes to move between items, so an inline search
 * field would fight the menu's own keyboard model. One search UI, two entry
 * points.
 */
export function SearchConversationsRow({ onSelect }: { onSelect: () => void }) {
  return (
    <>
      <MenuItem
        icon={<MaterialIcon name="search" boxSize="14px" />}
        fontSize="sm"
        color="text.secondary"
        onClick={onSelect}
      >
        Search conversations…
      </MenuItem>
      <MenuDivider />
    </>
  );
}

interface LoadMoreRowProps {
  readonly loaded: number;
  readonly total: number;
  readonly isLoading: boolean;
  readonly onLoadMore: () => void;
}

/**
 * The way down to conversations past the first page.
 *
 * Shows the counts: a bare "Load more" gives no clue that forty conversations
 * are missing. `closeOnSelect={false}` keeps the menu open, since this appends
 * to the list the user is reading.
 */
export function LoadMoreSessionsRow({ loaded, total, isLoading, onLoadMore }: LoadMoreRowProps) {
  return (
    <MenuItem
      icon={<MaterialIcon name="expand_more" boxSize="14px" />}
      fontSize="sm"
      color="text.secondary"
      isDisabled={isLoading}
      closeOnSelect={false}
      onClick={onLoadMore}
    >
      {isLoading ? 'Loading…' : `Load more (${loaded} of ${total})`}
    </MenuItem>
  );
}

interface SessionHistoryMenuProps extends ComponentProps<typeof SessionList> {
  readonly isLoadingSessions: boolean;
  readonly isLoadingMoreSessions: boolean;
  readonly hasMoreSessions: boolean;
  readonly totalSessions: number;
  readonly onSearchConversations?: () => void;
  readonly onLoadMore: () => void;
}

/**
 * Everything inside the history menu: the search entry point, the conversation
 * rows, and the way down to older pages.
 *
 * Composed here rather than in BirdChatPanel so the panel carries one element
 * instead of four conditionals — it sits at the linter's complexity ceiling,
 * where each added branch is counted against the whole component.
 */
export function SessionHistoryMenu(props: SessionHistoryMenuProps) {
  const {
    isLoadingSessions,
    isLoadingMoreSessions,
    hasMoreSessions,
    totalSessions,
    onSearchConversations,
    onLoadMore,
    ...listProps
  } = props;

  if (isLoadingSessions && listProps.sessions.length === 0) {
    return (
      <Flex justify="center" py={3}>
        <BowerSpinner size="sm" />
      </Flex>
    );
  }

  return (
    <>
      {onSearchConversations && <SearchConversationsRow onSelect={onSearchConversations} />}
      <SessionList {...listProps} />
      {hasMoreSessions && (
        <LoadMoreSessionsRow
          loaded={listProps.sessions.length}
          total={totalSessions}
          isLoading={isLoadingMoreSessions}
          onLoadMore={onLoadMore}
        />
      )}
    </>
  );
}
